import React, { useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useGlobalContext } from 'GlobalContext';
import FormFieldContext from '../FormFieldContext';
import { ConditionLinesFieldType, FieldData } from 'types/FieldTypes';
import FieldWrapper from '../FieldWrapper';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/free-solid-svg-icons';
import { ConditionLineType, serializeConditionLines } from '../../../views/settings/ConditionLineTypes';
import '../../../style/scss/live-edit.scss';
import '../../../style/scss/forms.scss';

const ConditionLinesField: React.FC<ConditionLinesFieldType & { data: FieldData, viewKey: string }> = ({
    name, label, data, helperText, tooltipText, shouldAutoFocus, alignment, isEditable, viewKey
}) => {
    const { t } = useTranslation();
    const { setUnsavedChanges } = useGlobalContext();
    const { editing, updatedData, setUpdatedData, handleSubmit } = useContext(FormFieldContext);
    const [lines, setLines] = useState<ConditionLineType[]>([]);

    // Load the condition lines of the document from the fetched data
    useEffect(() => {
        const existingLines = Array.isArray(data?.[name]) ? data[name] : [];
        setLines(existingLines.map((line: ConditionLineType) => ({ ...line })));
    }, [data, name]);

    // Render nothing when the document has no condition lines
    if (lines.length === 0) {
        return null;
    }

    // Updates the value of a line and registers the unsaved change
    const handleValueChange = (index: number, value: string) => {
        const updatedLines = lines.map((line, lineIndex) => lineIndex === index ? { ...line, value } : line);
        setLines(updatedLines);
        setUnsavedChanges(viewKey, true);
        setUpdatedData({ ...updatedData, [name]: serializeConditionLines(updatedLines) });
    };

    // Submits the values after pressing enter
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (handleSubmit) handleSubmit();
        }
    };

    return (
        <FieldWrapper
            name={name}
            label={label}
            tooltipText={tooltipText}
            helperText={helperText}
            isEditable={isEditable}
            alignment={alignment}>
            {editing ? (
                // Edit mode, the labels are fixed and only the values can be changed
                <div className='condition-lines-edit'>
                    {lines.map((line, index) => (
                        <div className='condition-line-row' key={index}>
                            <span className='condition-line-label'>{line.label}</span>
                            <input
                                type='text'
                                id={`${name}_${index}`}
                                name={`${name}_${index}`}
                                value={line.value}
                                onChange={event => handleValueChange(index, event.target.value)}
                                onKeyDown={handleKeyDown}
                                className='text'
                                autoFocus={shouldAutoFocus && index === 0}
                                data-1p-ignore='true'
                            />
                        </div>
                    ))}
                </div>
            ) : (
                // View mode, the labels and values in two aligned columns
                <div className='view-mode-wrapper condition-lines-view'>
                    <div className='condition-lines-list'>
                        {lines.map((line, index) => (
                            <div className='condition-line-row' key={index}>
                                <span className='condition-line-label'>{line.label}</span>
                                <span className='condition-line-value'>{line.value || '-'}</span>
                            </div>
                        ))}
                    </div>
                    {alignment === 'stacked' &&
                        <span className='edit-icon'>
                            <FontAwesomeIcon icon={faPen} />
                        </span>
                    }
                </div>
            )}
        </FieldWrapper>
    );
};

export default ConditionLinesField;
